import type { CellViewWire, ContactViewWire, OrderDraft, TeamId } from '@badger-sentinel/protocol';

import { byAlias } from './alias.js';
import type { Selection } from './selection.js';
import type {
  AssetLocationWire,
  ContactLocationWire,
  CyberNetworkViewWire,
  FriendlyAssetViewWire,
} from './view-types.js';

/**
 * The roster — the left rail's five groups (design 175–340).
 *
 * The design draws **friendly · enemy · cyber teams · friendly net · enemy net**.
 * All five are buildable from the wire the repository ships today:
 *
 * | Group | Source |
 * | --- | --- |
 * | FRIENDLY | `view.friendly` where `segment !== 'CYBER'` |
 * | ENEMY | `view.contacts` |
 * | CYBER TEAMS | `view.friendly` where `segment === 'CYBER'` |
 * | FRIENDLY NET | `view.cyber.networks` where `side === viewer.team` |
 * | ENEMY NET | `view.cyber.networks` where `side !== viewer.team` |
 *
 * The three cyber groups ship now rather than waiting for Phase 7. CYBER TEAMS
 * is `friendly` partitioned by a field that is on every asset, so it is not
 * speculative in any sense — and leaving those assets in FRIENDLY buries the
 * CYBER seat's own units in a list of satellites. The two network groups read
 * `cyber.networks[]`, which Phase 0.5 put on the wire *for this* and which
 * nothing else consumes until Phase 7; three phases of an unexercised projection
 * block is how a field rots. What stays Phase 7's is the cyber **map**, the
 * access ladder and the topology — none of which this rail draws.
 *
 * **No group ships as an empty heading.** A group with no rows renders nothing
 * at all, so a view whose enemy networks are still entirely unknown — every
 * fixture today — simply has no ENEMY NET section, and gains one the moment a
 * foothold or a cyber contact names one.
 *
 * ## Ordering
 *
 * Friendly rows sort by id, which is a real, stable, viewer-owned name. Contact
 * and network rows sort by `hash(alias)` (`model/alias.ts`) — never by array
 * position, which is a channel (must-not #5).
 */

export type RosterGroupId = 'FRIENDLY' | 'ENEMY' | 'CYBER_TEAMS' | 'FRIENDLY_NET' | 'ENEMY_NET';

export const ROSTER_GROUP_LABELS: Readonly<Record<RosterGroupId, string>> = Object.freeze({
  FRIENDLY: 'FRIENDLY',
  ENEMY: 'ENEMY',
  CYBER_TEAMS: 'CYBER TEAMS',
  FRIENDLY_NET: 'FRIENDLY NET',
  ENEMY_NET: 'ENEMY NET',
});

/** Canonical group order — the design's, and not the view's to decide. */
export const ROSTER_GROUP_ORDER: readonly RosterGroupId[] = Object.freeze([
  'FRIENDLY',
  'ENEMY',
  'CYBER_TEAMS',
  'FRIENDLY_NET',
  'ENEMY_NET',
] as const);

export type RosterSubject = 'ASSET' | 'CONTACT' | 'NETWORK';

/**
 * One row, flattened.
 *
 * Everything a row draws or a facet matches on is here, so the filter engine
 * never reaches back into the view and a contact row cannot accidentally acquire
 * a field a friendly asset has. `zone` is the row's one location word — a
 * region, a band, a network or `OFFMAP` — and it is what the ZONE facet, the map
 * focus and the "filter roster to this zone" context action all read.
 */
export interface RosterRow {
  readonly key: string;
  readonly group: RosterGroupId;
  readonly subject: RosterSubject;
  /** The asset id, the contact **alias**, or the network id. Never a true enemy id. */
  readonly id: string;
  readonly name: string;
  readonly typeId: string;
  readonly segment: 'SPACE' | 'GROUND' | 'CYBER';
  readonly zone: string;
  /** `NOMINAL` / `DEGRADED` / … for an asset; a *believed* status, or null, for a contact. */
  readonly status: string | null;
  /** Contacts only. `FOUND` / `FIXED` / `TRACKED` — never `HIDDEN`. */
  readonly rung: string | null;
  /** Friendly assets only: this seat may task it this turn. */
  readonly orderable: boolean;
  /**
   * Friendly assets only: an accepted order stands against it this turn.
   *
   * From the **order queue** — the whole set this seat wants stored — and not
   * from `FriendlyAssetView.onMission`, which is the HVA scoring flag of RB
   * §5.4.1 ("cleared by any manoeuvre"), true for almost every asset in an order
   * of battle and nothing at all to do with tasking. Drawing it as a TSK badge
   * would put a mark on thirteen of fourteen rows and mean nothing (M5-D10: a
   * binding with no source is deleted, never repurposed).
   *
   * Phase 2 read `orders.state.own.accepted` instead. The queue says the same
   * thing one round trip earlier, which is what the design's own `as.tasked`
   * reads, and it lights the moment the operator queues rather than when the
   * server agrees.
   */
  readonly tasked: boolean;
  /**
   * What clicking the row selects, or `null` for a row with nothing to open.
   *
   * Assets and contacts select themselves. A **network** selects its *router* —
   * which for the viewer's own enclave is one of its own assets, and is the
   * useful question ("what is the way in, and what state is it in?"). An enemy
   * enclave whose router is still `null`, or whose router is a cyber-contact
   * alias rather than an asset, selects nothing: a network is not one of the
   * three subjects the right rail draws, and Phase 7 owns enclave detail. Those
   * rows render as static rows rather than as buttons, so the affordance says so
   * without a caption.
   */
  readonly selects: Selection | null;
  /** The strings `search` matches against. Lower-cased once, here. */
  readonly haystack: string;
}

export interface RosterGroup {
  readonly id: RosterGroupId;
  readonly label: string;
  readonly rows: readonly RosterRow[];
}

/** An asset's or contact's one location word. */
export function zoneOf(location: AssetLocationWire | ContactLocationWire): string {
  switch (location.kind) {
    case 'ORBIT':
    case 'BAND':
      return location.band;
    case 'SLOT':
      return location.band;
    case 'TRANSIT':
      return location.to;
    case 'GROUND':
    case 'REGION':
      return location.region;
    case 'OFFMAP':
      return 'OFFMAP';
    case 'UNKNOWN':
      return 'UNKNOWN';
  }
}

function assetRow(
  asset: FriendlyAssetViewWire,
  group: RosterGroupId,
  tasked: ReadonlySet<string>,
): RosterRow {
  const zone = group === 'CYBER_TEAMS' ? asset.network : zoneOf(asset.location);
  return {
    key: 'A:' + asset.id,
    group,
    subject: 'ASSET',
    id: asset.id,
    name: asset.typeName,
    typeId: asset.typeId,
    segment: asset.segment,
    zone,
    status: asset.status,
    rung: null,
    orderable: asset.orderable,
    tasked: tasked.has(asset.id),
    selects: { kind: 'ASSET', id: asset.id },
    haystack: [asset.id, asset.typeId, asset.typeName, zone, asset.assignedCell]
      .join(' ')
      .toLowerCase(),
  };
}

function contactRow(contact: ContactViewWire, tasked: ReadonlySet<string>): RosterRow {
  const zone = zoneOf(contact.location);
  return {
    key: 'C:' + contact.id,
    group: 'ENEMY',
    subject: 'CONTACT',
    id: contact.id,
    name: contact.presentedTypeName,
    typeId: contact.presentedType,
    // A contact's segment is not on the wire; its location says which board it is
    // on and nothing more, which is all this is used for.
    segment: contact.location.kind === 'REGION' ? 'GROUND' : 'SPACE',
    zone,
    status: contact.believedStatus?.status ?? null,
    rung: contact.rung,
    orderable: false,
    tasked: tasked.has(contact.id),
    selects: { kind: 'CONTACT', id: contact.id },
    haystack: [contact.id, contact.presentedType, contact.presentedTypeName, zone]
      .join(' ')
      .toLowerCase(),
  };
}

function networkRow(network: CyberNetworkViewWire, group: RosterGroupId): RosterRow {
  const router = network.router;
  return {
    key: 'N:' + network.id,
    group,
    subject: 'NETWORK',
    id: network.id,
    name: network.name,
    typeId: 'NETWORK',
    segment: 'CYBER',
    zone: network.id,
    // A network has a **posture**, not a status, and the two do not belong in
    // one facet: `hardening` next to `NOMINAL` under a STATUS heading reads as a
    // fourth operational state. The posture is Phase 7's to draw, along with the
    // access ladder it belongs beside. It is still searchable — `haystack`
    // carries the router — and it is still withheld where the projection
    // withholds it (`hardening` is null for an enemy enclave whose router has
    // not been found; null there is unknown, never NORMAL).
    status: null,
    rung: null,
    orderable: false,
    tasked: false,
    // Phase 7 gave an enclave its own selection kind, so a network row selects
    // the network. Phase 2's stand-in — the router, when the router happened to
    // be one of ours, and nothing at all otherwise — meant a row could not be
    // selected on the enemy side and selected *something else* on ours.
    selects: { kind: 'NETWORK', id: network.id },
    haystack: [network.id, network.name, router ?? ''].join(' ').toLowerCase(),
  };
}

/**
 * Every row in the view, grouped and ordered. Groups with no rows are absent.
 *
 * Takes the queue as well as the view because a row shows both — what the picture
 * says about a unit, and whether this seat has tasked it. The two live in
 * different places and the rail needs them together.
 */
export function rosterOf(
  view: CellViewWire,
  queued: readonly OrderDraft[],
): readonly RosterGroup[] {
  const viewer: TeamId = view.viewer.team;
  const tasked = new Set(
    queued.flatMap((order) => [
      order.asset,
      ...(order.target.kind === 'CONTACT' ? [order.target.contact] : []),
      // Q24's arm names a friendly id, so a station under a queued HARDEN shows
      // as tasked the way a contact under a queued ENGAGE does. Without this the
      // one seat that can order defensively is the one seat whose roster does not
      // mark what it has ordered.
      ...(order.target.kind === 'OWN_ASSET' ? [order.target.asset] : []),
    ]),
  );

  const friendly = view.friendly
    .filter((asset) => asset.segment !== 'CYBER')
    .map((asset) => assetRow(asset, 'FRIENDLY', tasked))
    .sort((a, b) => a.id.localeCompare(b.id));

  const cyberTeams = view.friendly
    .filter((asset) => asset.segment === 'CYBER')
    .map((asset) => assetRow(asset, 'CYBER_TEAMS', tasked))
    .sort((a, b) => a.id.localeCompare(b.id));

  const enemy = byAlias(view.contacts, (contact) => contact.id).map((contact) =>
    contactRow(contact, tasked),
  );

  const ownNets = byAlias(
    view.cyber.networks.filter((net) => net.side === viewer),
    (net) => net.id,
  ).map((net) => networkRow(net, 'FRIENDLY_NET'));

  const enemyNets = byAlias(
    view.cyber.networks.filter((net) => net.side !== viewer),
    (net) => net.id,
  ).map((net) => networkRow(net, 'ENEMY_NET'));

  const groups: Record<RosterGroupId, readonly RosterRow[]> = {
    FRIENDLY: friendly,
    ENEMY: enemy,
    CYBER_TEAMS: cyberTeams,
    FRIENDLY_NET: ownNets,
    ENEMY_NET: enemyNets,
  };

  return ROSTER_GROUP_ORDER.filter((id) => groups[id].length > 0).map((id) => ({
    id,
    label: ROSTER_GROUP_LABELS[id],
    rows: groups[id],
  }));
}
